"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  Clock,
  Factory,
  Handshake,
  UsersRound,
} from "lucide-react";
import { getLocalizedPageContent } from "@/content/localized-pages";
import { t } from "@/content/i18n/messages";
import {
  defaultLocale,
  getLocalizedPathname,
  type Locale,
  type TranslatedLocale,
} from "@/lib/i18n";

const officeImage = "/images/company/office-dark.jpg";

interface AboutUsProps {
  locale?: Locale;
}

function isTranslatedLocale(locale: Locale): locale is TranslatedLocale {
  return locale !== defaultLocale;
}

const englishStory = `At Anchor Sprint Sdn Bhd, we specialize in delivering tailored technology solutions designed to help businesses thrive in a fast-changing digital landscape. With expertise spanning software development, system integration, cloud architecture, AI-driven innovation, and more, we empower organizations to optimize operations, embrace cutting-edge technologies, and achieve sustained growth.

Our leadership team brings over 16 years of extensive experience in the technology industry, providing a strong foundation of knowledge and insight to address the unique challenges of modern businesses. This wealth of expertise enables us to craft innovative solutions that not only meet current business needs but also anticipate future trends, ensuring our clients remain competitive in an evolving marketplace.

Our commitment to innovation, adaptability, and excellence ensures that we provide solutions that not only meet immediate business needs but also position our clients for future success. Whether through seamless software integration, smart manufacturing advancements, or strategic digital transformation, we are dedicated to driving measurable value and creating lasting impact.

Choose Anchor Sprint Sdn Bhd as your technology partner, and let our proven experience and expertise accelerate your journey to success.`;

export default function AboutUs({ locale = defaultLocale }: AboutUsProps) {
  const prefersReducedMotion = useReducedMotion();
  const translate = (text: string) => t(locale, text);

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const localizedContent = isTranslatedLocale(locale)
    ? getLocalizedPageContent("about-us", locale)
    : null;
  const storyContent = localizedContent
    ? [
        localizedContent.intro,
        ...localizedContent.sections.map((section) => section.body),
        localizedContent.summary,
      ].join("\n\n")
    : englishStory;
  const storyParagraphs = storyContent.split("\n\n");

  const milestones: Array<{ title: string; description: string }> = [
    {
      title: "Rooted in engineering",
      description:
        "Founded in Malaysia by a leadership team with over 16 years of technology industry experience across software development, system integration, and cloud architecture.",
    },
    {
      title: "Malaysia Digital certified",
      description:
        "Recognised under the Malaysia Digital initiative, giving our clients confidence in our governance, compliance, and delivery standards.",
    },
    {
      title: "An AI-first practice",
      description:
        "From software and cloud foundations, we expanded into AI agent development, Claude managed services, and practical AI training for enterprise teams.",
    },
    {
      title: "Serving enterprises across ASEAN",
      description:
        "Today we partner with organisations across Malaysia and ASEAN, turning AI strategy into deployed, working systems that create lasting impact.",
    },
  ];

  // Company credentials, text only (2026-10-08). The two programme lines are
  // each programme's approved wording and stay English in every locale
  // (data-i18n-skip, never passed through t()). No logos or badges here: the
  // OpenAI badge may never sit on a page that could carry the Claude Partner
  // Network badge, and neither programme is compared with the other.
  const credentials: Array<{
    label: string;
    detail: string;
    englishOnly?: boolean;
    href?: string;
  }> = [
    {
      label: "Certified in the Claude Partner Network",
      detail: "Anthropic's partner programme for firms that build and deploy on Claude.",
      englishOnly: true,
      href: "/partners/anthropic-claude-partner/",
    },
    {
      label: "10 Claude Certified Architects",
      detail: "Engineers certified to design and build production systems on Claude.",
    },
    {
      label: "OpenAI Select Partner",
      detail: "Our status in the OpenAI Partner Network, for ChatGPT rollout and OpenAI API builds.",
      englishOnly: true,
      href: "/partners/openai-partner/",
    },
    {
      label: "MDEC Malaysia Digital status",
      detail: "Recognised by the Malaysia Digital Economy Corporation since 2024.",
    },
    {
      label: "HRD Corp approved training provider",
      detail: "Our training in Malaysia can be claimed against your HRD Corp levy.",
    },
  ];

  const whyChoose: Array<{
    title: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      title: "Dedicated Support Team",
      description:
        "We provide comprehensive technical support and maintenance services with quick response times and personalized attention to your needs.",
      icon: UsersRound,
    },
    {
      title: "Strategic Partnerships",
      description:
        "Our strategic partnerships with leading technology providers enable us to deliver cutting-edge solutions and maintain high standards of service delivery.",
      icon: Handshake,
    },
    {
      title: "Proven Track Record",
      description:
        "With years of experience serving businesses across Malaysia and ASEAN, we have built a strong reputation for delivering reliable and innovative technology solutions.",
      icon: Clock,
    },
    {
      title: "Industry Expertise",
      description:
        "From manufacturing to retail, our deep understanding of each sector means tailored solutions that address industry-specific challenges and opportunities.",
      icon: Factory,
    },
  ];

  return (
    <div className="bg-background">
      {/* Unified page header */}
      <section className="bg-background pb-16 pt-24 md:pb-20 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translate("About Us")}
          </motion.p>
          <motion.h1
            {...reveal(0.08)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {translate("The people building")}{" "}
            <span className="emphasis-underline">
              {translate("AI agents in Malaysia")}
            </span>
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-5 max-w-2xl text-lg text-neutral-muted"
          >
            {translate(
              "Technologists, strategists, and designers in Malaysia, building tailored technology and AI solutions for enterprises across ASEAN.",
            )}
          </motion.p>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <Link
          href={getLocalizedPathname("/our-people/", locale)}
          className="inline-flex min-h-11 items-center font-medium text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
        >
          {translate("Meet our co-founders, Jazz Tong and Liew Jee Fong")}
        </Link>
      </div>

      {/* Our story — image + text */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
            <motion.div
              initial={prefersReducedMotion ? false : { y: 12 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
              className="overflow-hidden rounded-2xl"
            >
              <img
                src={officeImage}
                alt={translate("Anchor Sprint office at night, lit by screens and city lights")}
                className="h-full w-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={prefersReducedMotion ? false : { y: 12 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: [0.21, 0.6, 0.35, 1],
              }}
            >
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {translate("Who we are")}
              </p>
              <h2 className="mb-3 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {translate("Our")}{" "}
                <span className="emphasis-underline">{translate("Story")}</span>
              </h2>
              <p className="mb-6 max-w-2xl text-lg text-neutral-muted">
                {translate(
                  "Tailored technology and AI solutions, built by an experienced Malaysian team.",
                )}
              </p>
              <div className="space-y-4">
                {storyParagraphs.slice(0, 2).map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-base leading-relaxed text-neutral-muted"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Stat row — balances the photo column */}
              <div className="mt-8 grid grid-cols-3 gap-6 border-t border-border pt-6">
                <div>
                  <div className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
                    16+
                  </div>
                  <p className="mt-1 text-sm text-neutral-muted">
                    {translate("Years of technology leadership")}
                  </p>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
                    {translate("Certified")}
                  </div>
                  <p className="mt-1 text-sm text-neutral-muted">
                    {translate("Malaysia Digital status")}
                  </p>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
                    {translate("ASEAN")}
                  </div>
                  <p className="mt-1 text-sm text-neutral-muted">
                    {translate("Enterprises served across the region")}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credentials — text only, no badges */}
      <section className="border-t border-border bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Credentials")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("What we hold,")}{" "}
              <span className="emphasis-underline">
                {translate("in plain terms")}
              </span>
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "Five credentials you can check before you sign. Partner status does not imply endorsement of our services.",
              )}
            </p>
          </div>

          <ul className="max-w-4xl divide-y divide-border border-y border-border">
            {credentials.map(({ label, detail, englishOnly, href }) => (
              <li
                key={label}
                className="grid gap-1 py-5 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:gap-8"
              >
                <p className="font-display text-base font-semibold text-neutral-emphasis">
                  {href ? (
                    <Link
                      href={getLocalizedPathname(href, locale)}
                      className="underline decoration-border underline-offset-4 transition-colors hover:text-primary hover:decoration-primary"
                    >
                      {englishOnly ? (
                        <span data-i18n-skip>{label}</span>
                      ) : (
                        translate(label)
                      )}
                    </Link>
                  ) : englishOnly ? (
                    <span data-i18n-skip>{label}</span>
                  ) : (
                    translate(label)
                  )}
                </p>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {translate(detail)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Journey timeline — hairline + numbered milestone cards */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Milestones")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Our")}{" "}
              <span className="emphasis-underline">
                {translate("journey")}
              </span>{" "}
              {translate("so far")}
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-neutral-muted">
              {translate("Four chapters that shaped how we deliver today.")}
            </p>
          </div>

          <div className="relative mx-auto max-w-3xl">
            <div
              className="absolute bottom-4 left-[21px] top-4 w-px bg-border"
              aria-hidden
            />
            <ol className="space-y-8">
              {milestones.map(({ title, description }, index) => (
                <motion.li
                  key={title}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.08,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  className="relative pl-16"
                >
                  <span className="absolute left-0 top-1 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card font-mono text-sm text-primary">
                    0{index + 1}
                  </span>
                  <div className="rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border">
                    <h3 className="mb-2 font-display text-lg font-semibold text-neutral-emphasis">
                      {translate(title)}
                    </h3>
                    <p className="text-sm leading-relaxed text-neutral-muted">
                      {translate(description)}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Why choose Anchor Sprint */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Why us")}
            </p>
            <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-4xl">
              {translate("Why Choose")}{" "}
              <span className="emphasis-underline">
                {translate("Anchor Sprint")}
              </span>
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "Four reasons enterprises across ASEAN keep building with us.",
              )}
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            {whyChoose.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="flex h-full items-start gap-5 rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <h3 className="mb-2 font-display text-base font-semibold text-neutral-emphasis">
                    {translate(title)}
                  </h3>
                  <p className="text-sm leading-relaxed text-neutral-muted">
                    {translate(description)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-5xl">
            {translate("Ready to work with")}{" "}
            <span className="emphasis-underline">
              {translate("our team?")}
            </span>
          </h2>
          <p className="mt-5 text-lg text-neutral-muted">
            {translate(
              "Partner with Anchor Sprint to leverage our expertise in technology solutions, strategic partnerships, and proven track record to drive your business forward.",
            )}
          </p>
          <div className="mt-9">
            <Link
              href={getLocalizedPathname("/contact/", locale)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {translate("Meet with Us")}
            </Link>
          </div>
        </div>
      </section>

      {/* Registered entities */}
      <section className="border-t border-border bg-background py-8">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-muted">
            {translate("Registered entities")}
          </p>
          <p
            className="mt-2 text-sm text-neutral-muted"
            data-i18n-skip
          >
            Anchor Sprint Sdn Bhd (1535934-D) · Malaysia
            <span className="mx-2 hidden sm:inline">·</span>
            <br className="sm:hidden" />
            Anchor Sprint Pte. Ltd. (UEN 202632732Z) · Singapore
          </p>
        </div>
      </section>
    </div>
  );
}
